// Per-dialect synthesizer for CHECK constraint add/drop ops (v2 Slice B4).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B4.
//
// Each engine emits the dialect-correct ADD/DROP CONSTRAINT clause:
//   - mssql:  `ADD [CONSTRAINT name] CHECK (expr) [WITH NOCHECK]`
//             / `DROP CONSTRAINT name`.
//   - pgsql:  `ADD [CONSTRAINT name] CHECK (expr) [NO INHERIT]`
//             / `DROP CONSTRAINT name [CASCADE]`.
//   - mysql:  `ADD [CONSTRAINT name] CHECK (expr) [NOT ENFORCED]` (8.0.16+)
//             / `DROP CHECK name`. mysql uses `DROP CHECK <name>` rather
//             than `DROP CONSTRAINT` for portability across mysql 8.0.16
//             + 8.0.19+ (`DROP CONSTRAINT` is 8.0.19+).
//   - oracle: `ADD CONSTRAINT name CHECK (expr) [DEFERRABLE INITIALLY
//             {DEFERRED|IMMEDIATE}]`
//             / `DROP CONSTRAINT name [CASCADE]`.
//   - sqlite: not supported on ALTER → throws (DSE2-6). CHECK in CREATE
//             TABLE only; there is no automated recreate helper for this op
//             (the opt-in `sqliteRecreate` primitive only rewrites columns) —
//             the table must be rebuilt by hand.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { CheckConstraintDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

function rejectField(engine: SqlEngine, field: string): never {
  throw new DdlEditError(
    'invalid-edit',
    `applyDdlEdit: ${engine} CHECK constraint does not accept '${field}'`,
  );
}

function validateCheck(engine: SqlEngine, ck: CheckConstraintDef): void {
  if (!ck.expression || ck.expression.trim().length === 0) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: CHECK constraint requires a non-empty expression`,
    );
  }
  if (engine !== 'mssql' && ck.mssqlWithNoCheck !== undefined) {
    rejectField(engine, 'mssqlWithNoCheck');
  }
  if (engine !== 'postgres' && ck.pgsqlNoInherit !== undefined) {
    rejectField(engine, 'pgsqlNoInherit');
  }
  if (engine !== 'mysql' && ck.mysqlNotEnforced !== undefined) {
    rejectField(engine, 'mysqlNotEnforced');
  }
  if (engine !== 'oracle' && ck.deferrable !== undefined) {
    rejectField(engine, 'deferrable');
  }
}

export function synthesizeAddCheckConstraint(
  engine: SqlEngine,
  table: QualifiedName,
  ck: CheckConstraintDef,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … ADD CONSTRAINT `
        + `CHECK. Define the check constraint inline at CREATE TABLE time, or `
        + `rebuild the table manually (CREATE TABLE with the constraint, copy `
        + `the data across, DROP the old table, RENAME the new one) — the `
        + `opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  validateCheck(engine, ck);
  const tbl = tableText(engine, table);
  const constraintPrefix = ck.name ? `CONSTRAINT ${ck.name} ` : '';

  if (engine === 'mssql') {
    const noCheck = ck.mssqlWithNoCheck ? ' WITH NOCHECK' : '';
    // mssql syntax: `ALTER TABLE … WITH NOCHECK ADD CONSTRAINT … CHECK (…)`.
    // The WITH NOCHECK clause is positioned BEFORE the ADD keyword, not
    // after the CHECK expression — it modifies the ALTER, not the CHECK.
    return `ALTER TABLE ${tbl}${noCheck} ADD ${constraintPrefix}CHECK (${ck.expression})`;
  }

  if (engine === 'postgres') {
    const noInherit = ck.pgsqlNoInherit ? ' NO INHERIT' : '';
    return `ALTER TABLE ${tbl} ADD ${constraintPrefix}CHECK (${ck.expression})${noInherit}`;
  }

  if (engine === 'mysql') {
    const notEnforced = ck.mysqlNotEnforced ? ' NOT ENFORCED' : '';
    return `ALTER TABLE ${tbl} ADD ${constraintPrefix}CHECK (${ck.expression})${notEnforced}`;
  }

  // oracle
  let s = `ALTER TABLE ${tbl} ADD ${constraintPrefix}CHECK (${ck.expression})`;
  if (ck.deferrable !== undefined) {
    s += ` DEFERRABLE INITIALLY ${ck.deferrable === 'deferred' ? 'DEFERRED' : 'IMMEDIATE'}`;
  }
  return s;
}

export function synthesizeDropCheckConstraint(
  engine: SqlEngine,
  table: QualifiedName,
  constraintName: string,
  cascade: boolean | undefined,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … DROP CHECK. `
        + `Rebuild the table manually (CREATE TABLE without the constraint, `
        + `copy the data across, DROP the old table, RENAME the new one) — `
        + `the opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  if (!constraintName || constraintName.length === 0) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: ${engine} DROP CHECK constraint requires 'constraintName'`,
    );
  }
  const tbl = tableText(engine, table);

  if (engine === 'mysql') {
    // mysql 8.0.16 introduced `DROP CHECK <name>` as the recommended drop
    // idiom; `DROP CONSTRAINT` is 8.0.19+. CASCADE is not accepted in this
    // position on mysql — silently ignored.
    return `ALTER TABLE ${tbl} DROP CHECK ${constraintName}`;
  }

  const cas = (engine === 'postgres' || engine === 'oracle') && cascade ? ' CASCADE' : '';
  return `ALTER TABLE ${tbl} DROP CONSTRAINT ${constraintName}${cas}`;
}
